// hooks/useCreateNewReservation.ts
import { useDispatch } from 'react-redux';
import { baseStore } from '../store';
import moment from 'moment';
import Logger from '../helpers/logger';
import ErrorTracker from '../helpers/errorTracker';
import { vouchersApiSlice } from '../../api/voucher/voucherApiSlice';
import { couponRedemptionLoading, setRoomRentalVoucher } from '../slice/bookingCreation/bookingCreationSlice';
import { enqueueNotification } from '../slice/notification/notificationSlice';
import { t } from "i18next";
import { voucherRefusalMessage } from '../../util/voucherRefusal';


const fetchVoucherCall = async(voucherCode: string, locationId: string, groupId: string) => {    
    const response = await baseStore.dispatch(
        vouchersApiSlice.endpoints.getVoucherByCodeAndLocationAndGroup.initiate({
            voucherCode: voucherCode,
            locationId: locationId,
            groupId: groupId,
        })
    )
    if (!response || response.error) {
      // Kept as the request's error (status + body) so the catch below can
      // read why the code was refused (#3052) — not "Invalid API response".
      throw response?.error ?? new Error("Invalid API response");
    }
    return response;
};

export const useFetchVoucher = () => {
  const dispatch = useDispatch();

  const notifyRefused = (error: unknown) => {
    dispatch(couponRedemptionLoading({ loading: false }));
    // #3052: a code scoped to another station, expired, at its limit, ...
    // says so; a code that does not exist (404) stays "invalid".
    const refusal = voucherRefusalMessage(error);
    dispatch(
      enqueueNotification({
        severity: "error",
        message: t(refusal.key, refusal.params),
      })
    );
  };

  const fetchVoucher = async (voucherCode: string, locationId: string, groupId: string) => {
    try {
        const { data: voucher } = await fetchVoucherCall(voucherCode, locationId, groupId); // API call for each locationId


        // The backend refuses an expired code itself since #3052; this stays
        // as a guard and reads as "expired", not "invalid".
        if (!voucher || !voucher.validUntilDate || moment(voucher.validUntilDate).isBefore(moment())) {
          notifyRefused({ data: { reason: voucher ? "expired" : undefined } });
          return;
        }

        if (voucher) {
          dispatch(couponRedemptionLoading({ loading: false }));
          dispatch(
            enqueueNotification({
              severity: "success",
              message: t("book_location.success.coupon_applied"),
            })
          );
          dispatch(
            setRoomRentalVoucher({
              id: voucher.id,
              code: voucher.code,
              discountIsPercentage: voucher.discountIsPercentage,
              discountValue: voucher.discountValue
            })
          );
        }
        // Dispatch action after all groups are fetched
        // dispatch(setAllDepotsByLocation({ depots: allDepotsByLocation }));
    } catch (error) {
      notifyRefused(error);
      Logger.for('fetchVoucher').error(error);
      ErrorTracker.trackException(error instanceof Error ? error : new Error(JSON.stringify(error)));
      // Handle the error state or dispatch error actions if necessary
    }
  };

  return fetchVoucher;
};
